import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LayoutDashboard, ListOrdered, Package, Users,
  Truck, Warehouse, Layout, Boxes, Menu, X, Settings, ClipboardCheck, Bell, AlertTriangle, Images,
} from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useAdminI18n } from "@/lib/adminI18n";

const NAV_GROUPS = [
  {
    label: "Main",
    items: [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/admin", testid: "nav-dashboard", permission: "audit.view" },
      { key: "orders", label: "Orders", icon: ListOrdered, to: { pathname: "/admin", search: "?tab=orders" }, testid: "nav-orders", permission: "orders.manage" },
      { key: "products", label: "Products", icon: Package, to: { pathname: "/admin", search: "?tab=products" }, testid: "nav-products", permission: "products.manage" },
      { key: "customers", label: "Customers", icon: Users, to: "/admin/customers", testid: "nav-customers", permission: "customers.manage" },
    ],
  },
  {
    label: "Operations",
    items: [
      { key: "shipping", label: "Shipping Rates", icon: Truck, to: "/admin/shipping", testid: "nav-shipping", permission: "content.manage" },
      { key: "shipping-only", label: "Shipping Only", icon: Boxes, to: "/admin/shipping-only", testid: "nav-shipping-only", permission: "content.manage" },
      { key: "warehouses", label: "Warehouses", icon: Warehouse, to: "/admin/warehouses", testid: "nav-warehouses", permission: "content.manage" },
    ],
  },
  {
    label: "System",
    items: [
      { key: "content", label: "Content", icon: Layout, to: "/admin/content", testid: "nav-content", permission: "content.manage" },
      { key: "settings", label: "Settings", icon: Settings, to: "/admin/settings", testid: "nav-settings", permission: "settings.manage" },
      { key: "social-posts", label: "Shop For Less posts", icon: Images, to: "/admin/social-posts", testid: "nav-social-posts", permission: "products.manage" },
      { key: "approvals", label: "Approvals", icon: ClipboardCheck, to: "/admin/approvals", testid: "nav-approvals", permission: "approvals.manage" },
      { key: "notifications", label: "Notifications", icon: Bell, to: "/admin/notifications", testid: "nav-notifications", permission: "notifications.manage" },
    ],
  },
];

function resolveActiveKey(location) {
  if (location.pathname === "/admin") {
    const tab = new URLSearchParams(location.search).get("tab");
    return tab || "dashboard";
  }
  if (location.pathname === "/admin/shipping") return "shipping";
  if (location.pathname === "/admin/shipping-only") return "shipping-only";
  if (location.pathname === "/admin/warehouses") return "warehouses";
  if (location.pathname === "/admin/customers") return "customers";
  if (location.pathname === "/admin/content") return "content";
  if (location.pathname === "/admin/settings") return "settings";
  if (location.pathname === "/admin/social-posts") return "social-posts";
  if (location.pathname === "/admin/approvals") return "approvals";
  if (location.pathname === "/admin/notifications") return "notifications";
  return "";
}

function isTargetActive(itemKey, activeKey) {
  if (itemKey === "dashboard") return activeKey === "dashboard";
  return itemKey === activeKey;
}

function NavItems({ activeKey, onNavigate, mobile = false }) {
  const { t, isRTL } = useAdminI18n();
  const { hasPermission } = useAdminAuth();
  return NAV_GROUPS.map((group) => (
    <div key={group.label} className="space-y-2">
      <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/30">
        {t(`shell.${group.label.toLowerCase()}`, group.label)}
      </div>
      <div className="space-y-1">
        {group.items.filter((item) => hasPermission(item.permission)).map(({ key, label, icon: Icon, to, testid }) => {
          const active = isTargetActive(key, activeKey);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onNavigate(to)}
              data-testid={mobile ? `${testid}-mobile` : testid}
              className={`admin-shell-link w-full ${
                active ? "admin-shell-link-active" : "admin-shell-link-idle"
              } ${isRTL ? "flex-row-reverse" : ""}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#F58D4A]" : "text-white/45"}`} />
              <span className="truncate">{t(`shell.${key === "shipping-only" ? "shippingOnly" : key}`, label)}</span>
            </button>
          );
        })}
      </div>
    </div>
  ));
}

export default function AdminShell({
  title,
  description,
  eyebrow = "BOB Admin",
  actions,
  children,
}) {
  const { user, apiFetch } = useAdminAuth();
  const { t, isRTL, dir } = useAdminI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [securitySummary, setSecuritySummary] = useState(null);

  const activeKey = useMemo(() => resolveActiveKey(location), [location]);
  const warningBanner = useMemo(() => {
    const counts = securitySummary?.counts || {};
    if ((counts.pending_approvals || 0) > 0) {
      return t("settings.pendingApprovalsWarning", "{count} approval request(s) need review.").replace("{count}", counts.pending_approvals);
    }
    if ((counts.locked_logins || 0) > 0) {
      return t("settings.lockedLoginsWarning", "{count} login account(s) are temporarily locked.").replace("{count}", counts.locked_logins);
    }
    return "";
  }, [securitySummary, t]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    apiFetch("/admin/settings/security-summary")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setSecuritySummary(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleNavigate = (target) => {
    if (typeof target === "string") navigate(target);
    else navigate(target);
  };

  return (
    <main dir={dir} className={`admin-shell-root min-h-screen overflow-x-hidden bg-[#080808] text-white ${isRTL ? "admin-shell-rtl" : ""}`}>
      <div className={`flex min-h-screen ${isRTL ? "lg:flex-row-reverse" : ""}`}>
        <aside className="hidden lg:block lg:w-72 xl:w-80">
          <div className={`sticky top-0 flex h-screen flex-col ${isRTL ? "border-l" : "border-r"} border-white/8 bg-[#0a0a0a]/95 px-6 py-6 backdrop-blur-xl`}>
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="flex items-center gap-3 rounded-3xl border border-white/10 bg-white/[0.045] px-4 py-4 text-left transition hover:border-[#F37021]/40 hover:bg-white/[0.065]"
              data-testid="admin-brand"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F37021]/16 text-[#F58D4A] shadow-[0_16px_32px_rgba(243,112,33,0.18)]">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.28em] text-white/35">{eyebrow}</div>
                <div className="truncate text-base font-semibold text-white">{t("shell.console")}</div>
              </div>
            </button>

            <div className="mt-8 flex-1 space-y-6 overflow-y-auto pe-1">
              <NavItems activeKey={activeKey} onNavigate={handleNavigate} />
            </div>

            <div className="mt-6 space-y-4 rounded-3xl border border-white/10 bg-white/[0.045] p-4">
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/35">{t("shell.signedIn")}</div>
              <div className="mt-2 truncate text-sm font-semibold text-white">{user?.email || t("shell.adminUser")}</div>
              <div className="mt-1 text-xs text-white/45">
                {t("shell.secureAccess")}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-40 border-b border-white/8 bg-[#0a0a0a]/92 backdrop-blur-xl">
            <div className={`mx-auto flex w-full max-w-[96rem] items-start gap-3 px-4 py-4 sm:px-6 lg:px-8 ${isRTL ? "lg:flex-row-reverse" : ""}`}>
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="mt-1 inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-white/75 transition hover:border-[#F37021]/40 hover:text-white lg:hidden"
                aria-label={t("shell.openNav")}
                data-testid="admin-mobile-menu"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div className="min-w-0 flex-1">
                <div className={`text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F58D4A] ${isRTL ? "text-right" : ""}`}>
                  {eyebrow}
                </div>
                <div className={`mt-1 flex flex-col gap-3 xl:items-end xl:justify-between ${isRTL ? "xl:flex-row-reverse" : "xl:flex-row"}`}>
                  <div className={`min-w-0 ${isRTL ? "admin-force-right" : ""}`}>
                    <h1 className={`truncate text-2xl font-semibold text-white sm:text-[1.9rem] ${isRTL ? "text-right" : ""}`}>
                      {title}
                    </h1>
                    {description && (
                      <p className={`mt-1 max-w-3xl text-sm leading-6 text-white/55 ${isRTL ? "text-right" : ""}`}>
                        {description}
                      </p>
                    )}
                  </div>
                  <div className={`flex flex-wrap items-center gap-2 ${isRTL ? "justify-end" : ""}`}>
                    {actions}
                  </div>
                </div>
              </div>
            </div>
          </header>

          <section className="mx-auto w-full max-w-[96rem] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            {warningBanner && (
              <div className={`mb-5 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
                "border-amber-400/25 bg-amber-400/10 text-amber-100"
              } ${isRTL ? "flex-row-reverse text-right" : ""}`}>
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold">{warningBanner}</div>
                </div>
              </div>
            )}
            {children}
          </section>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-black/72 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-label={t("shell.closeOverlay")}
          />
          <aside className={`relative z-10 h-full w-[min(88vw,22rem)] ${isRTL ? "ms-auto border-l" : "border-r"} border-white/10 bg-[#0a0a0a] px-4 py-5 shadow-[0_25px_80px_rgba(0,0,0,0.45)]`}>
            <div className="flex items-center justify-between gap-3 rounded-3xl border border-white/10 bg-white/[0.045] px-4 py-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.28em] text-white/35">{eyebrow}</div>
                <div className="truncate text-base font-semibold text-white">{title}</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.05] text-white/70"
                aria-label={t("shell.closeNav")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 space-y-6 overflow-y-auto pb-24">
              <NavItems activeKey={activeKey} onNavigate={handleNavigate} mobile />
            </div>

            <div className="absolute inset-x-4 bottom-5 rounded-3xl border border-white/10 bg-white/[0.045] p-4">
              <div className="truncate text-sm font-semibold text-white">{user?.email || t("shell.adminUser")}</div>
              <div className="mt-1 text-xs text-white/45">{t("shell.mobilePinned")}</div>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
